import React, { useEffect, useState } from 'react';
import { Check, Loader2, Bell } from 'lucide-react';
import { brainStates, stateById } from '../../data/fastBrain';
import { MonthPattern, SuggestedSheets } from './CheckInExtras';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const lastWeek = () => Array.from({ length: 7 }, (_, i) => { const d = new Date(); d.setDate(d.getDate() - (6 - i)); return d; });

// Week strip of check-ins (also used read-only in Jill's Corner)
export const WeekStrip = ({ checkins, testId = 'fb-week' }) => {
  const byDate = Object.fromEntries((checkins || []).map((c) => [c.date, c]));
  const counts = {};
  (checkins || []).forEach((c) => { counts[c.state] = (counts[c.state] || 0) + 1; });
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return (
    <div data-testid={testId}>
      <div className="grid grid-cols-7 gap-1.5">
        {lastWeek().map((d) => {
          const c = byDate[iso(d)];
          const s = c && stateById(c.state);
          return (
            <div key={iso(d)} className="text-center" title={c?.note || ''} data-testid={`${testId}-${iso(d)}`}>
              <p className="font-body text-[10px] uppercase text-stone-500">{d.toLocaleDateString(undefined, { weekday: 'short' })}</p>
              <div className="mx-auto mt-1 w-12 h-12 rounded-full flex items-center justify-center text-[9px] leading-none font-body font-bold uppercase" style={s ? { background: s.bg, color: s.color, border: `2px solid ${s.color}` } : { border: '2px dashed #d6d3d1', color: '#a8a29e' }}>
                {s ? s.name : '–'}
              </div>
            </div>
          );
        })}
      </div>
      {top && <p className="mt-3 font-body text-sm text-stone-600" data-testid={`${testId}-pattern`}>This week your brain has felt mostly <strong style={{ color: stateById(top[0]).color }}>{stateById(top[0]).name}</strong> ({top[1]} of your last {Math.min(checkins.length, 7)} check-in{Math.min(checkins.length, 7) === 1 ? '' : 's'}).</p>}
    </div>
  );
};

export default function DailyCheckIn({ userId, onOpenSheet }) {
  const [checkins, setCheckins] = useState([]);
  const [pick, setPick] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [view, setView] = useState('week');
  const [reminder, setReminder] = useState(false);
  const today = iso(new Date());

  useEffect(() => {
    fetch(`${API}/fast-brain/checkins?user_id=${encodeURIComponent(userId)}`).then((r) => (r.ok ? r.json() : { checkins: [] }))
      .then((d) => {
        const list = d.checkins || [];
        setCheckins(list);
        setReminder(!!d.reminder);
        const t = list.find((c) => c.date === today);
        if (t) { setPick(t.state); setNote(t.note || ''); setSaved(true); }
      }).catch(() => {});
  }, [userId, today]);

  const toggleReminder = async (e) => {
    const enabled = e.target.checked;
    setReminder(enabled);
    const res = await fetch(`${API}/fast-brain/reminder`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: userId, enabled }) }).catch(() => null);
    if (!res?.ok) setReminder(!enabled);
  };

  const save = async () => {
    if (!pick) return;
    setSaving(true);
    const res = await fetch(`${API}/fast-brain/checkin`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ user_id: userId, date: today, state: pick, note }) }).catch(() => null);
    setSaving(false);
    if (res?.ok) { setSaved(true); setCheckins((p) => [{ date: today, state: pick, note }, ...p.filter((c) => c.date !== today)]); }
  };

  return (
    <section className="mt-12 max-w-2xl mx-auto text-left bg-white border border-stone-200 rounded-3xl p-6" data-testid="fb-checkin">
      <p className="font-body text-xs font-semibold uppercase tracking-[0.2em] text-[#6b3a5e]">Daily brain check-in</p>
      <p className="font-body text-sm text-stone-600 mb-4">How does your brain feel today? Pick one and add a few words if you like.</p>
      <div className="flex flex-wrap gap-2 mb-3">
        {brainStates.map((s) => (
          <button key={s.id} type="button" onClick={() => { setPick(s.id); setSaved(false); }} className="px-4 py-2 rounded-full font-body text-sm font-semibold border-2 transition-colors" style={pick === s.id ? { background: s.color, color: '#fff', borderColor: s.color } : { background: s.bg, color: s.color, borderColor: 'transparent' }} data-testid={`fb-checkin-${s.id}`}>
            {s.name}
          </button>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <input value={note} onChange={(e) => { setNote(e.target.value); setSaved(false); }} maxLength={300} placeholder="Why? (optional) — e.g. slept badly, big deadline…" className="flex-1 bg-[#faf7f2] border border-stone-200 rounded-full px-4 py-2 font-body text-sm focus:outline-none focus:border-[#6b3a5e]" data-testid="fb-checkin-note" />
        <button type="button" onClick={save} disabled={!pick || saving || saved} className="inline-flex items-center justify-center gap-1.5 bg-[#6b3a5e] text-white rounded-full px-5 py-2 font-body text-sm disabled:opacity-60" data-testid="fb-checkin-save">
          {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <Check size={14} /> : null} {saved ? 'Checked in' : 'Save check-in'}
        </button>
      </div>
      {saved && pick && <SuggestedSheets stateId={pick} onOpen={onOpenSheet} />}
      <div className="flex items-center gap-2 mb-4">
        {['week', 'month'].map((v) => (
          <button key={v} type="button" onClick={() => setView(v)} className={`px-3 py-1 rounded-full font-body text-xs border ${view === v ? 'bg-[#6b3a5e] text-white border-[#6b3a5e]' : 'border-stone-200 text-stone-600'}`} data-testid={`fb-view-${v}`}>
            {v === 'week' ? 'This week' : 'Last 30 days'}
          </button>
        ))}
      </div>
      {view === 'week' ? <WeekStrip checkins={checkins.filter((c) => c.date >= iso(lastWeek()[0]))} /> : <MonthPattern checkins={checkins} />}
      <label className="mt-6 pt-4 border-t border-stone-100 flex items-center gap-3 cursor-pointer" data-testid="fb-reminder">
        <input type="checkbox" checked={reminder} onChange={toggleReminder} className="accent-[#6b3a5e] w-4 h-4" data-testid="fb-reminder-toggle" />
        <Bell size={15} className="text-[#6b3a5e]" />
        <span className="font-body text-sm text-stone-700">Email me a gentle reminder each morning (8am) if I haven’t checked in yet</span>
      </label>
    </section>
  );
}
